import { memo, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Icon, Modal } from "@repo/ui";

import ainaAvatarUrl from "../../../assets/images/aina/aina-icon-modal.webp?url";
import { getAinaIntroSkipModalLayoutStyles } from "./styles";
import skipStyles from "./AinaIntroSkipConfirm.module.scss";

export type AinaIntroSkipConfirmModalProps = {
  isModalOpen: boolean;
  isMobile: boolean;
  onClose: () => void;
  onGetInsights: () => void;
};

function cls(...parts: (string | false | undefined | null)[]): string {
  return parts.filter(Boolean).join(" ");
}

export const AinaIntroSkipConfirmModal = memo(
  function AinaIntroSkipConfirmModal({
    isModalOpen,
    isMobile,
    onClose,
    onGetInsights,
  }: AinaIntroSkipConfirmModalProps) {
    const { t } = useTranslation("ainaIntro");

    const classNames = useMemo(
      () => ({
        shell: cls(skipStyles.shell, isMobile && skipStyles.shellMobile),
        title: cls(skipStyles.title, isMobile && skipStyles.titleMobile),
        body: cls(skipStyles.body, isMobile && skipStyles.bodyMobile),
        cta: cls(skipStyles.cta, isMobile && skipStyles.ctaMobile),
      }),
      [isMobile],
    );

    const layoutStyles = useMemo(
      () => getAinaIntroSkipModalLayoutStyles(isMobile),
      [isMobile],
    );

    const body = (
      <div className={classNames.shell}>
        <button
          type="button"
          className={skipStyles.closeButton}
          onClick={onClose}
          aria-label={t("skip_confirm.a11y_go_back")}
        >
          <Icon name="close" size={24} color="#ffffff" />
        </button>

        <div className={skipStyles.avatarSection}>
          <img
            src={ainaAvatarUrl}
            alt=""
            className={skipStyles.avatar}
            width={250}
            height={250}
            decoding="async"
          />
        </div>

        <h2 className={classNames.title}>{t("skip_confirm.title")}</h2>

        <p className={classNames.body}>
          <Trans
            ns="ainaIntro"
            i18nKey="skip_confirm.body"
            components={{
              insights: <span className={skipStyles.emphasis} />,
              goals: <span className={skipStyles.emphasis} />,
              aina: <span className={skipStyles.emphasis} />,
            }}
          />
        </p>

        <button
          type="button"
          onClick={onGetInsights}
          className={classNames.cta}
        >
          <span className={skipStyles.ctaLabel}>
            {t("skip_confirm.cta_insights")}
          </span>
        </button>
      </div>
    );

    return (
      <Modal
        isModalOpen={isModalOpen}
        showHeader={false}
        showFooter={false}
        overlayClassName={skipStyles.overlay}
        width="100%"
        height="auto"
        justifyContent="center"
        alignItems="center"
        onClose={onClose}
        body={body}
        styles={layoutStyles}
      />
    );
  },
);
